import React, { createContext, useContext, useReducer, useCallback, ReactNode, useMemo, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  getAvailablePackages,
  getPurchasedPackages,
} from "@/services/student/packages.service";
import { AvailablePackage } from "@/services/student/packages.interfaces";
import { useAppToast } from "@/hooks/useAppToast";
import { customerInfoSchema } from "./schemas";
import { createOrder, applyVoucher, updateCustomerInfo as updateCustomerInfoService, commitOrder } from "@/services/student/checkout/checkout.service";
import { Order, ErrorCodes, CheckoutProcessResult, PaymentSelectionKey, PaymentSession, parsePaymentSelectionKey, toPaymentSelectionKey } from "@/services/student/checkout/checkout.interfaces";
import { CheckoutError } from "@/services/student/checkout/checkout.api";
import { recordUserBehaviorEvent } from "@/services/analytics/userBehaviorEvents.api";
import { usePaymentMethods } from "./payments/hooks/usePaymentMethods";
import type { PromoCodeState } from "./checkoutPromoTypes";

function getCheckoutErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof CheckoutError) return error.message;
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return fallback;
}

// =============== Query Keys ===============

export const CHECKOUT_QUERY_KEYS = {
  packages: ['checkout', 'packages'] as const,
  package: (id: string) => ['checkout', 'package', id] as const,
} as const;

// =============== Estado do Contexto ===============

interface CustomerInfo {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

type PaymentPhase = 'idle' | 'committing' | 'awaiting_payment' | 'completed' | 'failed';

interface CheckoutState {
  selectedPaymentKey: PaymentSelectionKey | null;
  paymentPhase: PaymentPhase;
  paymentSession: PaymentSession | null;
  paymentError: string | null;
  customerInfo: CustomerInfo;
  promoCode: PromoCodeState;
  isApplyingPromoCode: boolean;
  orderId: string | null;
  orderSubtotal: number | null; // Preço do pedido vindo da order
  orderDiscount: number | null; // Desconto total da order
  orderTotal: number | null; // Total da order
  isInitializingOrder: boolean;
  isUpdatingCustomer: boolean;
  isApplyingVoucher: boolean;
  isCommittingOrder: boolean;
  isPackageAlreadyPurchased: boolean;
  isPackageUnavailableForPurchase: boolean;
}

// =============== Actions para o Reducer ===============

enum ActionType {
  SET_PAYMENT_METHOD = 'SET_PAYMENT_METHOD',
  SET_PAYMENT_PHASE = 'SET_PAYMENT_PHASE',
  SET_PAYMENT_SESSION = 'SET_PAYMENT_SESSION',
  SET_PAYMENT_ERROR = 'SET_PAYMENT_ERROR',
  UPDATE_CUSTOMER_INFO = 'UPDATE_CUSTOMER_INFO',
  SET_PROMO_CODE = 'SET_PROMO_CODE',
  APPLY_PROMO_CODE_START = 'APPLY_PROMO_CODE_START',
  APPLY_PROMO_CODE_SUCCESS = 'APPLY_PROMO_CODE_SUCCESS',
  APPLY_PROMO_CODE_ERROR = 'APPLY_PROMO_CODE_ERROR',
  CLEAR_PROMO_CODE = 'CLEAR_PROMO_CODE',
  SET_ORDER_ID = 'SET_ORDER_ID',
  SYNC_ORDER = 'SYNC_ORDER',
  SET_INITIALIZING_ORDER = 'SET_INITIALIZING_ORDER',
  SET_UPDATING_CUSTOMER = 'SET_UPDATING_CUSTOMER',
  SET_APPLYING_VOUCHER = 'SET_APPLYING_VOUCHER',
  SET_COMMITTING_ORDER = 'SET_COMMITTING_ORDER',
  SET_PACKAGE_ALREADY_PURCHASED = 'SET_PACKAGE_ALREADY_PURCHASED',
  SET_PACKAGE_UNAVAILABLE_FOR_PURCHASE = 'SET_PACKAGE_UNAVAILABLE_FOR_PURCHASE',
}

type CheckoutAction =
  | { type: ActionType.SET_PAYMENT_METHOD; payload: PaymentSelectionKey }
  | { type: ActionType.SET_PAYMENT_PHASE; payload: PaymentPhase }
  | { type: ActionType.SET_PAYMENT_SESSION; payload: PaymentSession | null }
  | { type: ActionType.SET_PAYMENT_ERROR; payload: string | null }
  | { type: ActionType.UPDATE_CUSTOMER_INFO; payload: Partial<CustomerInfo> }
  | { type: ActionType.SET_PROMO_CODE; payload: string }
  | { type: ActionType.APPLY_PROMO_CODE_START }
    | { type: ActionType.APPLY_PROMO_CODE_SUCCESS; payload: { discount: number; discountType: 'percentage' | 'fixedAmount' | 'freeShipping'; discountValue: number } }
  | { type: ActionType.APPLY_PROMO_CODE_ERROR; payload: string }
  | { type: ActionType.CLEAR_PROMO_CODE }
  | { type: ActionType.SET_ORDER_ID; payload: string | null }
  | { type: ActionType.SYNC_ORDER; payload: Order }
  | { type: ActionType.SET_INITIALIZING_ORDER; payload: boolean }
  | { type: ActionType.SET_UPDATING_CUSTOMER; payload: boolean }
  | { type: ActionType.SET_APPLYING_VOUCHER; payload: boolean }
  | { type: ActionType.SET_COMMITTING_ORDER; payload: boolean }
  | { type: ActionType.SET_PACKAGE_ALREADY_PURCHASED; payload: boolean }
  | { type: ActionType.SET_PACKAGE_UNAVAILABLE_FOR_PURCHASE; payload: boolean };

// Reducer function
const checkoutReducer = (state: CheckoutState, action: CheckoutAction): CheckoutState => {
  switch (action.type) {
    case ActionType.SET_PAYMENT_METHOD:
      return {
        ...state,
        selectedPaymentKey: action.payload,
        paymentError: null,
      };
    case ActionType.SET_PAYMENT_PHASE:
      return {
        ...state,
        paymentPhase: action.payload,
      };
    case ActionType.SET_PAYMENT_SESSION:
      return {
        ...state,
        paymentSession: action.payload,
      };
    case ActionType.SET_PAYMENT_ERROR:
      return {
        ...state,
        paymentError: action.payload,
      };
    case ActionType.UPDATE_CUSTOMER_INFO:
      return {
        ...state,
        customerInfo: {
          ...state.customerInfo,
          ...action.payload
        }
      };
    case ActionType.SET_PROMO_CODE: {
      const codeChanged = action.payload !== state.promoCode.code;
      return {
        ...state,
        promoCode: {
          ...state.promoCode,
          code: action.payload,
          error: null,
          applied: codeChanged ? false : state.promoCode.applied,
          ...(codeChanged
            ? { discount: 0, discountType: undefined, discountValue: undefined }
            : {}),
        },
      };
    }
    case ActionType.APPLY_PROMO_CODE_START:
      return {
        ...state,
        isApplyingPromoCode: true,
        promoCode: {
          ...state.promoCode,
          error: null,
        },
      };
    case ActionType.APPLY_PROMO_CODE_SUCCESS:
      return {
        ...state,
        promoCode: {
          ...state.promoCode,
          applied: true,
          discount: action.payload.discount,
          discountType: action.payload.discountType,
          discountValue: action.payload.discountValue,
          error: null,
        },
        isApplyingPromoCode: false,
      };
    case ActionType.APPLY_PROMO_CODE_ERROR:
      return {
        ...state,
        isApplyingPromoCode: false,
        promoCode: {
          ...state.promoCode,
          error: action.payload,
        },
      };
    case ActionType.CLEAR_PROMO_CODE:
      return {
        ...state,
        promoCode: {
          code: "",
          applied: false,
          discount: 0,
          discountType: undefined,
          discountValue: undefined,
          error: null,
        },
      };
    case ActionType.SET_ORDER_ID:
      return {
        ...state,
        orderId: action.payload
      };
    case ActionType.SYNC_ORDER: {
      // Armazenar informações do voucher
      let voucherDiscount = 0;
      let voucherDiscountType: 'percentage' | 'fixedAmount' | 'freeShipping' | undefined = undefined;
      let voucherDiscountValue: number | undefined = undefined;
      
      // Só processar voucher se ele existir e tiver código
      // O backend só retorna voucher quando há VoucherId e VoucherCode no order
      // e há uma redenção ativa
      if (action.payload.voucher && action.payload.voucher.code) {
        voucherDiscountType = action.payload.voucher.discountType;
        voucherDiscountValue = action.payload.voucher.discountValue;
        
        if (action.payload.voucher.discountType === 'percentage') {
          voucherDiscount = action.payload.voucher.discountValue;
        } else if (action.payload.voucher.discountType === 'fixedAmount') {
          // Se for valor fixo, converter para percentual baseado no subtotal (para cálculos)
          voucherDiscount = action.payload.subtotal > 0
            ? (action.payload.voucher.discountValue / action.payload.subtotal) * 100
            : 0;
        } else if (action.payload.voucher.discountType === 'freeShipping') {
          // FreeShipping não aplica desconto no valor, apenas no frete
          voucherDiscount = 0;
        }
      }
      
      return {
        ...state,
        customerInfo: action.payload.customerInfo || state.customerInfo,
        promoCode: action.payload.voucher && action.payload.voucher.code
          ? {
              code: action.payload.voucher.code,
              applied: action.payload.voucher.applied === true,
              discount: voucherDiscount,
              discountType: voucherDiscountType,
              discountValue: voucherDiscountValue,
              error: null,
            }
          : state.promoCode,
        orderSubtotal: action.payload.subtotal ?? state.orderSubtotal,
        orderDiscount: action.payload.discount ?? state.orderDiscount,
        orderTotal: action.payload.total ?? state.orderTotal
      };
    }
    case ActionType.SET_INITIALIZING_ORDER:
      return {
        ...state,
        isInitializingOrder: action.payload
      };
    case ActionType.SET_UPDATING_CUSTOMER:
      return {
        ...state,
        isUpdatingCustomer: action.payload
      };
    case ActionType.SET_APPLYING_VOUCHER:
      return {
        ...state,
        isApplyingVoucher: action.payload
      };
    case ActionType.SET_COMMITTING_ORDER:
      return {
        ...state,
        isCommittingOrder: action.payload
      };
    case ActionType.SET_PACKAGE_ALREADY_PURCHASED:
      return {
        ...state,
        isPackageAlreadyPurchased: action.payload
      };
    case ActionType.SET_PACKAGE_UNAVAILABLE_FOR_PURCHASE:
      return {
        ...state,
        isPackageUnavailableForPurchase: action.payload
      };
    default:
      return state;
  }
};

// =============== Interface do Contexto ===============

interface CheckoutContextType {
  // Estado de UI
  state: CheckoutState;
  packageId: string | undefined;
  
  // Dados do pacote (React Query)
  packageInfo: AvailablePackage | undefined;
  isLoading: boolean;
  error: Error | null;
  
  // Métodos de pagamento
  setPaymentMethod: (key: PaymentSelectionKey) => void;
  completePaymentFlow: () => void;
  resetPaymentAttempt: (message?: string) => void;
  
  // Métodos de informações do cliente
  updateCustomerInfo: (info: Partial<CustomerInfo>) => void;
  
  // Métodos de código promocional
  setPromoCode: (code: string) => void;
  applyPromoCode: (code?: string) => Promise<boolean>;
  clearPromoCode: () => void;
  
  // Métodos de checkout
  processCheckout: () => Promise<CheckoutProcessResult>;
  
  // Validação de formulários
  registerCustomerInfoValidation: (triggerFn: () => Promise<boolean>) => void;
  validateCustomerInfo: () => Promise<boolean>;
  
  // Helpers
  getFinalPrice: () => number;
  isFormValid: () => boolean;
  canProcessCheckout: () => boolean;
  
  // Métodos de formatação
  formatCurrency: (value: number) => string;
  formatPercentage: (value: number) => string;
  formatDiscountValue: (price: number, discount: number) => string;
  formatDiscountFull: (price: number, discount: number) => string;
}

// =============== Criação do Contexto ===============

const CheckoutContext = createContext<CheckoutContextType | undefined>(undefined);

export const useCheckoutContext = () => {
  const context = useContext(CheckoutContext);
  if (context === undefined) {
    throw new Error("useCheckoutContext must be used within a CheckoutProvider");
  }
  return context;
};

// =============== Provider do Contexto ===============

interface CheckoutProviderProps {
  children: ReactNode;
  packageId?: string;
  voucherCode?: string;
}

export const CheckoutProvider: React.FC<CheckoutProviderProps> = ({
  children,
  packageId,
  voucherCode,
}) => {
  const toast = useAppToast();
  
  // Store voucher code in stable reference to avoid unnecessary re-renders
  const stableVoucherCode = useMemo(() => voucherCode, [voucherCode]);
  
  // Refs para métodos de validação dos formulários
  const customerInfoValidationRef = React.useRef<(() => Promise<boolean>) | null>(null);
  
  const registerCustomerInfoValidation = useCallback((triggerFn: () => Promise<boolean>) => {
    customerInfoValidationRef.current = triggerFn;
  }, []);
  
  // Estado inicial
  const initialState: CheckoutState = {
    selectedPaymentKey: null,
    paymentPhase: 'idle',
    paymentSession: null,
    paymentError: null,
    customerInfo: {
      firstName: '',
      lastName: '',
      email: '',
      phone: ''
    },
    promoCode: {
      code: "",
      applied: false,
      discount: 0,
      discountType: undefined,
      discountValue: undefined,
      error: null,
    },
    isApplyingPromoCode: false,
    orderId: null,
    orderSubtotal: null,
    orderDiscount: null,
    orderTotal: null,
    isInitializingOrder: false,
    isUpdatingCustomer: false,
    isApplyingVoucher: false,
    isCommittingOrder: false,
    isPackageAlreadyPurchased: false,
    isPackageUnavailableForPurchase: false
  };

  // Estado para o reducer (apenas UI state)
  const [state, dispatch] = useReducer(checkoutReducer, initialState);

  const validateCustomerInfo = useCallback(async (): Promise<boolean> => {
    if (customerInfoValidationRef.current) {
      try {
        const valid = await customerInfoValidationRef.current();
        if (!valid) {
          toast.error("Revise os campos em destaque — há dados faltando ou incorretos.");
          return false;
        }
      } catch {
        toast.error("Revise os campos em destaque — há dados faltando ou incorretos.");
        return false;
      }
    }

    const customerInfoResult = customerInfoSchema.safeParse(state.customerInfo);
    if (!customerInfoResult.success) {
      toast.error("Revise os campos em destaque — há dados faltando ou incorretos.");
      return false;
    }

    return true;
  }, [state.customerInfo, toast]);

  const { data: paymentMethods } = usePaymentMethods();

  useEffect(() => {
    if (!paymentMethods?.length || state.selectedPaymentKey) return;

    const preferred =
      paymentMethods.find((m) => m.provider === "MercadoPago" && m.method === "Pix") ||
      paymentMethods[0];

    dispatch({
      type: ActionType.SET_PAYMENT_METHOD,
      payload: toPaymentSelectionKey(preferred.provider, preferred.method),
    });
  }, [paymentMethods, state.selectedPaymentKey]);

  // Estabilizar packageId para evitar re-renderizações desnecessárias
  const stablePackageId = useMemo(() => packageId, [packageId]);

  // Estabilizar queryFn com useCallback para evitar re-execução
  // Isso previne que o React Query pense que é uma nova query a cada render
  const queryFn = useCallback(() => {
    return getAvailablePackages();
  }, []); // Sem dependências - função estável

  // React Query para carregar pacote (idempotência automática)
  // IMPORTANTE: Configurações para evitar chamadas recursivas:
  // - refetchOnMount: false - não refaz a query quando o componente remonta
  // - refetchOnWindowFocus: false - não refaz quando a janela ganha foco
  // - staleTime: 5 minutos - considera os dados válidos por 5 minutos
  const { data: packages, isLoading, error } = useQuery({
    queryKey: CHECKOUT_QUERY_KEYS.packages,
    queryFn: queryFn, // Usar função estabilizada
    enabled: !!stablePackageId, // Só carrega se tiver packageId
    refetchOnMount: false, // Evita refetch quando o componente remonta
    refetchOnWindowFocus: false, // Evita refetch quando a janela ganha foco
    refetchOnReconnect: false, // Evita refetch quando reconecta
    staleTime: 1000 * 60 * 5, // 5 minutos - dados considerados válidos
  });

  // Encontrar o pacote específico - usar packages diretamente (React Query já estabiliza)
  const packageInfo = useMemo(() => {
    if (!stablePackageId || !packages) return undefined;
    return packages.find((pkg) => pkg.id === stablePackageId);
  }, [stablePackageId, packages]);

  // Initialize order when package is confirmed available (skip createOrder if already owned)
  useEffect(() => {
    if (!stablePackageId || state.orderId || isLoading || !packages) return;
    if (state.isPackageAlreadyPurchased || state.isPackageUnavailableForPurchase) return;

    const initializeOrder = async () => {
      try {
        dispatch({ type: ActionType.SET_INITIALIZING_ORDER, payload: true });

        try {
          const purchased = await getPurchasedPackages();
          if (purchased.some((pkg) => pkg.id === stablePackageId && pkg.isValidNow === true)) {
            dispatch({ type: ActionType.SET_PACKAGE_ALREADY_PURCHASED, payload: true });
            return;
          }
        } catch (purchasedLookupError) {
          console.error("Failed to pre-check purchased packages:", purchasedLookupError);
          // Fall through to createOrder; PACKAGE_ALREADY_PURCHASED remains the fallback.
        }

        if (!packageInfo) {
          dispatch({ type: ActionType.SET_PACKAGE_UNAVAILABLE_FOR_PURCHASE, payload: true });
          return;
        }

        const response = await createOrder(stablePackageId);

        if (response.success && response.order) {
          dispatch({ type: ActionType.SET_ORDER_ID, payload: response.order.id });
          dispatch({ type: ActionType.SYNC_ORDER, payload: response.order });
        }
      } catch (error) {
        console.error("Failed to initialize order:", error);

        if (
          error instanceof CheckoutError &&
          error.errorCode === ErrorCodes.PACKAGE_ALREADY_PURCHASED
        ) {
          dispatch({ type: ActionType.SET_PACKAGE_ALREADY_PURCHASED, payload: true });
        } else if (
          error instanceof CheckoutError &&
          error.errorCode === ErrorCodes.PACKAGE_PROMOTION_UNAVAILABLE
        ) {
          dispatch({ type: ActionType.SET_PACKAGE_UNAVAILABLE_FOR_PURCHASE, payload: true });
        } else {
          const errorMessage =
            error instanceof Error
              ? error.message
              : "Não conseguimos preparar o pedido. Atualize a página e tente de novo.";
          toast.error(errorMessage);
        }
      } finally {
        dispatch({ type: ActionType.SET_INITIALIZING_ORDER, payload: false });
      }
    };

    void initializeOrder();
  }, [
    stablePackageId,
    state.orderId,
    isLoading,
    packages,
    packageInfo,
    state.isPackageAlreadyPurchased,
    state.isPackageUnavailableForPurchase,
    toast,
  ]);

  // Track if voucher from URL has been applied to prevent duplicate applications
  const autoAppliedVoucherRef = useRef<string | null>(null);

  // Automatically apply voucher from URL when order is ready
  useEffect(() => {
    // Conditions: orderId exists, voucherCode exists, voucher not already applied, order not initializing
    if (!state.orderId || !stableVoucherCode || state.isInitializingOrder) {
      return;
    }

    // Skip if this voucher was already applied automatically
    if (autoAppliedVoucherRef.current === stableVoucherCode) {
      return;
    }

    // Skip if a different voucher is already applied
    if (state.promoCode.applied && state.promoCode.code !== stableVoucherCode) {
      return;
    }

    const applyVoucherFromUrl = async () => {
      if (!state.orderId) return;
      const orderId = state.orderId;

      try {
        dispatch({ type: ActionType.SET_APPLYING_VOUCHER, payload: true });
        dispatch({ type: ActionType.SET_PROMO_CODE, payload: stableVoucherCode });
        
        const response = await applyVoucher(orderId, stableVoucherCode);
        
        const voucher = response.voucher || response.order?.voucher;
        
        if (response.success && voucher) {
          // Convert discountValue to percentage if needed
          let discountPercent = 0;
          if (voucher.discountType === 'percentage') {
            discountPercent = voucher.discountValue;
          } else if (voucher.discountType === 'fixedAmount') {
            const currentSubtotal = state.orderSubtotal || packageInfo?.price || 0;
            discountPercent = currentSubtotal > 0
              ? (voucher.discountValue / currentSubtotal) * 100
              : 0;
          } else if (voucher.discountType === 'freeShipping') {
            discountPercent = 0;
          }
          
          dispatch({ 
            type: ActionType.APPLY_PROMO_CODE_SUCCESS, 
            payload: {
              discount: discountPercent,
              discountType: voucher.discountType,
              discountValue: voucher.discountValue
            }
          });
          
          if (response.order) {
            dispatch({ type: ActionType.SYNC_ORDER, payload: response.order });
          }
          
          // Mark as auto-applied
          autoAppliedVoucherRef.current = stableVoucherCode;
          
          toast.success(`Voucher ${stableVoucherCode} aplicado automaticamente com sucesso!`);
        } else {
          const message =
            response.message ||
            `Não conseguimos aplicar o cupom ${stableVoucherCode}. Confira o código e tente de novo.`;
          dispatch({ type: ActionType.APPLY_PROMO_CODE_ERROR, payload: message });
          toast.error(message);
        }
      } catch (error) {
        console.error("Failed to apply voucher from URL:", error);
        const errorMessage = getCheckoutErrorMessage(
          error,
          `Não conseguimos aplicar o cupom ${stableVoucherCode}. Confira o código e tente de novo.`
        );
        dispatch({ type: ActionType.APPLY_PROMO_CODE_ERROR, payload: errorMessage });
        toast.error(errorMessage);
      } finally {
        dispatch({ type: ActionType.SET_APPLYING_VOUCHER, payload: false });
      }
    };

    applyVoucherFromUrl();
  }, [state.orderId, stableVoucherCode, state.isInitializingOrder, state.promoCode.applied, state.promoCode.code, state.orderSubtotal, packageInfo, toast]);

  const setPaymentMethod = useCallback((key: PaymentSelectionKey): void => {
    dispatch({ type: ActionType.SET_PAYMENT_METHOD, payload: key });
  }, []);

  const completePaymentFlow = useCallback((): void => {
    dispatch({ type: ActionType.SET_PAYMENT_PHASE, payload: 'completed' });
  }, []);

  const resetPaymentAttempt = useCallback((message?: string): void => {
    dispatch({ type: ActionType.SET_PAYMENT_PHASE, payload: 'idle' });
    dispatch({ type: ActionType.SET_PAYMENT_SESSION, payload: null });
    dispatch({ type: ActionType.SET_PAYMENT_ERROR, payload: message ?? null });
  }, []);

  const updateCustomerInfo = useCallback((info: Partial<CustomerInfo>): void => {
    dispatch({ type: ActionType.UPDATE_CUSTOMER_INFO, payload: info });
  }, []);

  // Debounced update customer info to API
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    // Skip if no orderId or customerInfo is empty
    if (!state.orderId || !state.customerInfo.firstName || !state.customerInfo.lastName || !state.customerInfo.email || !state.customerInfo.phone) {
      return;
    }

    // Clear previous timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Set new timeout
    debounceTimeoutRef.current = setTimeout(async () => {
      if (!state.orderId) return; // Guard clause
      
      try {
        dispatch({ type: ActionType.SET_UPDATING_CUSTOMER, payload: true });
        const response = await updateCustomerInfoService(state.orderId, state.customerInfo);
        
        if (response.success && response.order) {
          dispatch({ type: ActionType.SYNC_ORDER, payload: response.order });
        }
      } catch (error) {
        console.error("Failed to update customer info:", error);
        // Don't show toast for debounced updates to avoid spam
      } finally {
        dispatch({ type: ActionType.SET_UPDATING_CUSTOMER, payload: false });
      }
    }, 500); // 500ms debounce

    // Cleanup on unmount
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, [state.orderId, state.customerInfo.firstName, state.customerInfo.lastName, state.customerInfo.email, state.customerInfo.phone]);

  const setPromoCode = useCallback((code: string): void => {
    dispatch({ type: ActionType.SET_PROMO_CODE, payload: code });
  }, []);

  const applyPromoCode = useCallback(async (code?: string): Promise<boolean> => {
    const voucherCode = (code?.trim() || state.promoCode.code.trim()).toUpperCase();

    if (!state.orderId) {
      const message = "Aguarde a preparação do pedido para aplicar o cupom.";
      dispatch({ type: ActionType.APPLY_PROMO_CODE_ERROR, payload: message });
      toast.error(message);
      return false;
    }

    if (state.isInitializingOrder) {
      const message = "O pedido ainda está sendo preparado. Aguarde alguns segundos e tente de novo.";
      dispatch({ type: ActionType.APPLY_PROMO_CODE_ERROR, payload: message });
      toast.error(message);
      return false;
    }

    if (!voucherCode) {
      const message = "Digite o código do cupom para aplicar.";
      dispatch({ type: ActionType.APPLY_PROMO_CODE_ERROR, payload: message });
      toast.error(message);
      return false;
    }

    if (
      state.promoCode.applied &&
      state.promoCode.code.toUpperCase() === voucherCode
    ) {
      return true;
    }

    if (
      stableVoucherCode &&
      autoAppliedVoucherRef.current === stableVoucherCode &&
      voucherCode !== stableVoucherCode
    ) {
      toast.warning("Você aplicou um cupom diferente do link de promoção.");
    }

    try {
      dispatch({ type: ActionType.APPLY_PROMO_CODE_START });
      dispatch({ type: ActionType.SET_APPLYING_VOUCHER, payload: true });

      if (voucherCode !== state.promoCode.code) {
        dispatch({ type: ActionType.SET_PROMO_CODE, payload: voucherCode });
      }

      if (voucherCode !== stableVoucherCode) {
        autoAppliedVoucherRef.current = null;
      }

      const response = await applyVoucher(state.orderId, voucherCode);
      const voucher = response.voucher || response.order?.voucher;

      if (response.success && voucher) {
        let discountPercent = 0;
        if (voucher.discountType === "percentage") {
          discountPercent = voucher.discountValue;
        } else if (voucher.discountType === "fixedAmount") {
          const currentSubtotal = state.orderSubtotal || packageInfo?.price || 0;
          discountPercent =
            currentSubtotal > 0
              ? (voucher.discountValue / currentSubtotal) * 100
              : 0;
        }

        dispatch({
          type: ActionType.APPLY_PROMO_CODE_SUCCESS,
          payload: {
            discount: discountPercent,
            discountType: voucher.discountType,
            discountValue: voucher.discountValue,
          },
        });

        if (response.order) {
          dispatch({ type: ActionType.SYNC_ORDER, payload: response.order });
        }

        if (state.paymentSession) {
          dispatch({ type: ActionType.SET_PAYMENT_SESSION, payload: null });
          dispatch({ type: ActionType.SET_PAYMENT_PHASE, payload: "idle" });
        }

        toast.success(response.message || "Cupom aplicado com sucesso!");
        return true;
      }

      const message =
        response.message || "Cupom inválido ou expirado. Confira o código e tente de novo.";
      dispatch({ type: ActionType.APPLY_PROMO_CODE_ERROR, payload: message });
      toast.error(message);
      return false;
    } catch (error) {
      console.error("Error applying promo code:", error);
      const errorMessage = getCheckoutErrorMessage(
        error,
        "Não conseguimos aplicar o cupom. Tente de novo em instantes."
      );
      dispatch({ type: ActionType.APPLY_PROMO_CODE_ERROR, payload: errorMessage });
      toast.error(errorMessage);
      return false;
    } finally {
      dispatch({ type: ActionType.SET_APPLYING_VOUCHER, payload: false });
    }
  }, [
    toast,
    state.orderId,
    state.promoCode.code,
    state.promoCode.applied,
    state.isInitializingOrder,
    state.orderSubtotal,
    state.paymentSession,
    stableVoucherCode,
    packageInfo?.price,
  ]);

  const clearPromoCode = useCallback((): void => {
    dispatch({ type: ActionType.CLEAR_PROMO_CODE });
  }, []);

  // Métodos de formatação
  const formatCurrency = useCallback((value: number): string => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'R$ 0,00';
    }
    return `R$ ${value.toFixed(2).replace('.', ',')}`;
  }, []);

  const formatPercentage = useCallback((value: number): string => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0%';
    }
    // Limitar a 2 casas decimais
    const roundedValue = Math.round(value * 100) / 100;
    return `${roundedValue.toFixed(2)}%`;
  }, []);

  const formatDiscountValue = useCallback((price: number, discount: number): string => {
    if (!price || !discount || discount <= 0) {
      return formatCurrency(0);
    }
    const discountAmount = price * (discount / 100);
    return formatCurrency(discountAmount);
  }, [formatCurrency]);

  const formatDiscountFull = useCallback((price: number, discount: number): string => {
    if (!price || !discount || discount <= 0) {
      return '';
    }
    const discountValue = formatDiscountValue(price, discount);
    return `-${formatPercentage(discount)} (${discountValue})`;
  }, [formatDiscountValue, formatPercentage]);

  // Helpers (definidos antes de processCheckout para serem usados dentro dele)
  const getFinalPrice = useCallback((): number => {
    // Se temos orderTotal do backend, usar ele diretamente (já inclui todos os descontos aplicados)
    // O backend já calcula o total corretamente com voucher aplicado
    if (state.orderTotal !== null && state.orderTotal !== undefined) {
      return state.orderTotal;
    }
    
    // Se temos o subtotal da order mas não temos total, calcular com descontos
    // Isso só acontece se o voucher ainda não foi aplicado
    if (state.orderSubtotal !== null && state.orderSubtotal !== undefined) {
      let priceAfterOrderDiscount = state.orderSubtotal;
      
      // Primeiro: aplicar desconto fixo da order (se houver)
      // orderDiscount pode ser um valor fixo (R$) ou percentual (%)
      // Se for menor que o subtotal, provavelmente é valor fixo
      if (state.orderDiscount !== null && state.orderDiscount !== undefined && state.orderDiscount > 0) {
        if (state.orderDiscount < state.orderSubtotal) {
          // Valor fixo: subtrair diretamente
          priceAfterOrderDiscount = state.orderSubtotal - state.orderDiscount;
        } else {
          // Percentual: aplicar como porcentagem
          priceAfterOrderDiscount = state.orderSubtotal * (1 - state.orderDiscount / 100);
        }
      }
      
      // Segundo: aplicar desconto promocional percentual (sempre percentual)
      // Mas só se o voucher ainda não foi aplicado (não está no orderTotal)
      const promoDiscount = state.promoCode.applied ? state.promoCode.discount : 0;
      const finalPrice = priceAfterOrderDiscount * (1 - promoDiscount / 100);
      
      return Math.max(0, finalPrice);
    }
    
    // Fallback: usar preço do pacote (comportamento antigo)
    if (!packageInfo?.price) return 0;
    
    const basePrice = packageInfo.price;
    const packageDiscount = packageInfo.discount || 0;
    const promoDiscount = state.promoCode.applied ? state.promoCode.discount : 0;
    
    // Aplicar desconto do pacote primeiro
    const priceAfterPackageDiscount = basePrice * (1 - packageDiscount / 100);
    
    // Aplicar desconto do código promocional
    const finalPrice = priceAfterPackageDiscount * (1 - promoDiscount / 100);
    
    return Math.max(0, finalPrice);
  }, [packageInfo, state.promoCode, state.orderSubtotal, state.orderDiscount, state.orderTotal]);

  const isFormValid = useCallback((): boolean => {
    return (
      state.customerInfo.firstName.trim() !== '' &&
      state.customerInfo.lastName.trim() !== '' &&
      state.customerInfo.email.trim() !== '' &&
      state.customerInfo.phone.trim() !== '' &&
      packageInfo !== undefined
    );
  }, [state.customerInfo, packageInfo]);

  const canProcessCheckout = useCallback((): boolean => {
    if (!packageInfo) {
      return false;
    }

    const customerInfoValid = (
      state.customerInfo.firstName.trim() !== '' &&
      state.customerInfo.lastName.trim() !== '' &&
      state.customerInfo.email.trim() !== '' &&
      state.customerInfo.email.includes('@') &&
      state.customerInfo.phone.trim() !== ''
    );

    return customerInfoValid && state.selectedPaymentKey !== null;
  }, [packageInfo, state.customerInfo, state.selectedPaymentKey]);


  const processCheckout = useCallback(async (): Promise<CheckoutProcessResult> => {
    try {
      if (!packageInfo) {
        toast.error("Informações do pacote estão faltando");
        return { status: 'validation_failed' };
      }

      if (!state.selectedPaymentKey) {
        toast.error("Selecione um método de pagamento");
        return { status: 'validation_failed' };
      }

      const paymentSelection = parsePaymentSelectionKey(state.selectedPaymentKey);
      if (!paymentSelection) {
        toast.error("Método de pagamento inválido");
        return { status: 'validation_failed' };
      }

      // Forçar validação dos formulários React Hook Form para mostrar erros nos campos
      let hasValidationErrors = false;

      if (customerInfoValidationRef.current) {
        try {
          const customerValid = await customerInfoValidationRef.current();
          if (!customerValid) {
            hasValidationErrors = true;
          }
        } catch {
          // Erro na validação - considerar como inválido
          hasValidationErrors = true;
        }
      }
      
      const customerInfoResult = customerInfoSchema.safeParse(state.customerInfo);
      
      if (!customerInfoResult.success) {
        hasValidationErrors = true;
      }

      if (hasValidationErrors) {
        toast.error("Revise os campos em destaque — há dados faltando ou incorretos.");
        return { status: 'validation_failed' };
      }

      // Ensure order exists
      let currentOrderId = state.orderId;
      if (!currentOrderId) {
        dispatch({ type: ActionType.SET_INITIALIZING_ORDER, payload: true });
        try {
          const createResponse = await createOrder(packageInfo.id);
          if (!createResponse.success || !createResponse.order) {
            toast.error("Falha ao criar pedido");
            return { status: 'failed', message: "Falha ao criar pedido" };
          }
          currentOrderId = createResponse.order.id;
          dispatch({ type: ActionType.SET_ORDER_ID, payload: currentOrderId });
          dispatch({ type: ActionType.SYNC_ORDER, payload: createResponse.order });
        } catch {
          toast.error("Falha ao criar pedido");
          return { status: 'failed', message: "Falha ao criar pedido" };
        } finally {
          dispatch({ type: ActionType.SET_INITIALIZING_ORDER, payload: false });
        }
      }

      // Update customer info
      dispatch({ type: ActionType.SET_UPDATING_CUSTOMER, payload: true });
      try {
        await updateCustomerInfoService(currentOrderId, state.customerInfo);
      } catch (error) {
        console.error("Failed to update customer info:", error);
        // Continue even if update fails
      } finally {
        dispatch({ type: ActionType.SET_UPDATING_CUSTOMER, payload: false });
      }

      // Apply voucher if there's a pending code before payment
      if (state.promoCode.code.trim() && !state.promoCode.applied) {
        const applied = await applyPromoCode(state.promoCode.code.trim());
        if (!applied) {
          toast.error("Corrija o cupom promocional antes de continuar o pagamento.");
          return { status: "validation_failed" };
        }
      }

      dispatch({ type: ActionType.SET_COMMITTING_ORDER, payload: true });
      dispatch({ type: ActionType.SET_PAYMENT_PHASE, payload: 'committing' });
      try {
        const commitResponse = await commitOrder(
          currentOrderId,
          paymentSelection.provider,
          paymentSelection.method
        );

        if (commitResponse.success && commitResponse.order) {
          dispatch({ type: ActionType.SYNC_ORDER, payload: commitResponse.order });
        }

        if (commitResponse.success && !commitResponse.requiresPayment) {
          if (packageInfo) {
            recordUserBehaviorEvent("PurchaseCompleted", {
              packageId: packageInfo.id,
              paymentMethod: state.selectedPaymentKey,
              voucherApplied: String(state.promoCode.applied),
            });
          }
          dispatch({ type: ActionType.SET_PAYMENT_PHASE, payload: 'completed' });
          return { status: 'completed' };
        }

        if (commitResponse.success && commitResponse.requiresPayment && commitResponse.paymentSession) {
          dispatch({ type: ActionType.SET_PAYMENT_SESSION, payload: commitResponse.paymentSession });
          dispatch({ type: ActionType.SET_PAYMENT_PHASE, payload: 'awaiting_payment' });
          return {
            status: 'awaiting_payment',
            session: commitResponse.paymentSession,
          };
        }

        const message = commitResponse.message || "Falha ao processar checkout";
        toast.error(message);
        dispatch({ type: ActionType.SET_PAYMENT_PHASE, payload: 'failed' });
        dispatch({ type: ActionType.SET_PAYMENT_ERROR, payload: message });
        return { status: 'failed', message };
      } catch (error) {
        console.error("Error committing order:", error);
        const errorMessage = error instanceof Error ? error.message : "Falha ao processar checkout";
        toast.error(errorMessage);
        dispatch({ type: ActionType.SET_PAYMENT_PHASE, payload: 'failed' });
        dispatch({ type: ActionType.SET_PAYMENT_ERROR, payload: errorMessage });
        return { status: 'failed', message: errorMessage };
      } finally {
        dispatch({ type: ActionType.SET_COMMITTING_ORDER, payload: false });
      }
    } catch (error) {
      console.error("Error processing checkout:", error);

      let errorMessage = "Falha ao processar checkout";
      if (error instanceof Error) {
        errorMessage = `Erro: ${error.message}`;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      toast.error(errorMessage);
      dispatch({ type: ActionType.SET_PAYMENT_PHASE, payload: 'failed' });
      dispatch({ type: ActionType.SET_PAYMENT_ERROR, payload: errorMessage });
      return { status: 'failed', message: errorMessage };
    }
  }, [packageInfo, state, toast, applyPromoCode]);

  // Valor do contexto
  const value: CheckoutContextType = useMemo(() => ({
    state,
    packageId,
    packageInfo,
    isLoading,
    error: error as Error | null,
    setPaymentMethod,
    completePaymentFlow,
    resetPaymentAttempt,
    updateCustomerInfo,
    setPromoCode,
    applyPromoCode,
    clearPromoCode,
    processCheckout,
    registerCustomerInfoValidation,
    validateCustomerInfo,
    getFinalPrice,
    isFormValid,
    canProcessCheckout,
    formatCurrency,
    formatPercentage,
    formatDiscountValue,
    formatDiscountFull
  }), [state, packageId, packageInfo, isLoading, error, setPaymentMethod, completePaymentFlow, resetPaymentAttempt, updateCustomerInfo, setPromoCode, applyPromoCode, clearPromoCode, processCheckout, registerCustomerInfoValidation, validateCustomerInfo, getFinalPrice, isFormValid, canProcessCheckout, formatCurrency, formatPercentage, formatDiscountValue, formatDiscountFull]);

  return (
    <CheckoutContext.Provider value={value}>
      {children}
    </CheckoutContext.Provider>
  );
};
